import _ from 'lodash';
import { PropType } from 'vue';
import mixins from 'vue-typed-mixins';

import {
  EOperator,
  E勘定科目カテゴリ,
  E有無フラグ,
  E科目別計画明細ConfigType,
  E補助科目フラグ,
  E計画表示モード,
} from '@/openapi/enum';
import { 勘定科目ListItemResponse, 科目別計画明細Config } from '@/openapi/user';

import { InputableTextMixin } from '@/components/inputableText/InputableText';
import KeystrokeInputableText from '@/components/keystrokeInputableText/KeystrokeInputableText.vue';
import C決算期TableHeader from '@/components/決算期TableHeader/決算期TableHeader.vue';
import C科目別TableRow from '@/components/科目別TableRow/科目別TableRow.vue';

import MoneyUnitLabelMixin from '@/entries/user/mixins/MoneyUnitLabelMixin';
import PlbscfKeystrokeInputableTextMixin, {
  PlbscfInputableTextId,
} from '@/entries/user/mixins/PlbscfKeystrokeInputableTextMixin';
import 事業所Mixin from '@/entries/user/mixins/事業所Mixin';
import 業績予測Mixin from '@/entries/user/mixins/業績予測Mixin';
import 計画三表TableMixin from '@/entries/user/mixins/計画三表TableMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';

import { MONTHS_OF_YEAR } from '@/classes/common/Constants';
import 決算期Utility, { 決算期TableHeader } from '@/classes/common/決算期Utility';
import { PlbscfYearItem, PlbscfYearValue, 科目別計画明細_CONFIG_COUNT } from '@/classes/service/AbstractPlbscfService';
import { Pl, 科目別計画, 科目別計画ListItem } from '@/classes/service/Abstract計画PlbscfService';
import 業績予測Service from '@/classes/service/モニタリング/業績予測Service';
import 計画PlbscfService from '@/classes/service/計画/計画PlbscfService';

type 科目別計画明細InputFormula = {
  nos: (number | undefined)[];
  operators: (EOperator | undefined)[];
};

type 科目別計画明細DialogConfig = Pick<Required<科目別計画明細Config>, 'type' | 'fixedValue'> & {
  formula: 科目別計画明細InputFormula;
  copy勘定科目Pk?: number;
};

type ItemFocus = {
  id: number;
  index: number;
};

const FORMULA_NO_LENGTH = 4;

export default mixins(
  InputableTextMixin,
  MoneyUnitLabelMixin,
  PlbscfKeystrokeInputableTextMixin,
  事業所Mixin,
  業績予測Mixin,
  計画三表TableMixin,
  計画書Mixin
).extend({
  name: 'C科目別計画',

  components: {
    KeystrokeInputableText,
    C決算期TableHeader,
    C科目別TableRow,
  },

  props: {
    is業績予測: {
      type: Boolean,
      default: false,
    },
    pl: {
      type: Object as PropType<Pl>,
      required: true,
    },
    科目別計画: {
      type: Object as PropType<科目別計画>,
      required: true,
    },
    header: {
      type: Array as PropType<決算期TableHeader[]>,
      required: true,
    },
    勘定科目s: {
      type: Array as PropType<勘定科目ListItemResponse[]>,
      required: true,
    },
  },

  data(): {
    configDialog: {
      visible: boolean;
      input: 科目別計画明細DialogConfig | null;
      listItem: 科目別計画ListItem | null;
      同科目ListItems: 科目別計画ListItem[];
    };
    e科目別計画明細ConfigType: typeof E科目別計画明細ConfigType;
    eOperator: typeof EOperator;
    itemFocus: ItemFocus | null;
    isFocused: boolean;
  } {
    return {
      configDialog: {
        visible: false,
        input: null,
        listItem: null,
        同科目ListItems: [],
      },
      e科目別計画明細ConfigType: E科目別計画明細ConfigType,
      eOperator: EOperator,
      itemFocus: null,
      isFocused: false,
    };
  },

  computed: {
    isFixedValueConfig(): boolean {
      return this.configDialog.input?.type === E科目別計画明細ConfigType.固定値;
    },
    isFormulaConfig(): boolean {
      return this.configDialog.input?.type === E科目別計画明細ConfigType.計算式;
    },
    is科目Config(): boolean {
      return this.configDialog.input?.type === E科目別計画明細ConfigType.科目選択;
    },
    isYearConfig(): boolean {
      return this.計画書.c計画表示モード === E計画表示モード.年次計画;
    },
    config勘定科目Options(): 勘定科目ListItemResponse[] {
      return this.勘定科目s.filter(
        (k) =>
          k.c補助科目フラグ === E補助科目フラグ.通常 &&
          [E勘定科目カテゴリ.損益, E勘定科目カテゴリ.製造原価].includes(k.cカテゴリ)
      );
    },
    visibleListItems(): 科目別計画ListItem[] {
      return this.is業績予測
        ? 業績予測Service.getVisible科目別ListBy基準値(this.科目別計画.list, this.vモニタリング業績予測基準値)
        : this.科目別計画.list;
    },
    map(): (PlbscfInputableTextId | null)[][] {
      const map: PlbscfInputableTextId[][] = [];
      this.科目別計画.list.forEach((listItem, listItemIndex) => {
        const ids: PlbscfInputableTextId[] = [];
        if (this.getConfig(listItem) === undefined || !this.isVisible科目別計画ListItem(listItem)) return;
        ids.push({ listItemIndex });
        this.addYearIds(
          ids,
          listItemIndex,
          listItem.yearItems,
          this.readonlyMonthIndex,
          !this.isEditableListItem(listItem)
        );
        if (ids.length > 0) map.push(ids);
      });
      return map;
    },
    firstColspan(): number {
      const headerColumnSize = this.is業績予測 ? 7 : 6;
      return headerColumnSize + this.header.length;
    },
    exclusive科目別計画明細ConfigTypes(): E科目別計画明細ConfigType[] {
      let exclusions: E科目別計画明細ConfigType[] = [];
      if (this.is業績予測) {
        // モニタリング
        exclusions = [E科目別計画明細ConfigType.前年値];
        if (this.モニタリング会計期間 === this.計画書.c計画0期の決算期)
          exclusions.push(E科目別計画明細ConfigType.前年同月値);
      } else {
        // 計画
        exclusions =
          this.計画書.c計画表示モード === E計画表示モード.年次計画
            ? [E科目別計画明細ConfigType.前年値月平均]
            : [E科目別計画明細ConfigType.前年値];
        if (this.計画書.c計画表示モード !== E計画表示モード.月次計画第1期)
          exclusions.push(E科目別計画明細ConfigType.前年同月値);
      }
      return exclusions;
    },
    cannotSaveConfig(): boolean {
      const type = this.configDialog.input?.type;
      return (
        type === undefined ||
        this.exclusive科目別計画明細ConfigTypes.includes(type) ||
        (type === E科目別計画明細ConfigType.科目選択 && this.configDialog.input?.copy勘定科目Pk === undefined)
      );
    },
    disableMonths(): boolean[] {
      return this.header?.filter((h) => h.month).map((h) => h.disabled!);
    },
  },

  filters: {
    configTypeLabel(config: 科目別計画明細Config): string {
      return 計画PlbscfService.getConfigTypeLabel(config);
    },
  },

  mounted(): void {
    this.setFirstId();
  },

  methods: {
    getConfig(listItem: 科目別計画ListItem) {
      let config = listItem.config!;
      if (this.isYearConfig) {
        config = listItem.yConfig!;
      }
      return config;
    },
    get補助科目(listItem: 科目別計画ListItem) {
      if (this.isYearConfig) {
        return listItem.y補助科目!;
      }
      return listItem.補助科目!;
    },
    get通常科目(listItem: 科目別計画ListItem) {
      if (this.isYearConfig) {
        return listItem.y通常科目!;
      }
      return listItem.通常科目!;
    },
    get計算項目No(listItem: 科目別計画ListItem) {
      if (this.isYearConfig) {
        return listItem.y計算項目No!;
      }
      return listItem.計算項目No!;
    },
    isVisible科目別計画ListItem(listItem: 科目別計画ListItem): boolean {
      return 計画PlbscfService.isVisible科目別計画ListItem(listItem, this.isYearConfig);
    },
    isEditableListItem(listItem: 科目別計画ListItem): boolean {
      return this.getConfig(listItem)?.type === E科目別計画明細ConfigType.直接入力;
    },
    canAddVisibleRow(listItem: 科目別計画ListItem): boolean {
      return (
        this.getConfig(listItem) !== undefined && this.get補助科目(listItem).itemCount < 科目別計画明細_CONFIG_COUNT
      );
    },
    canDeleteVisibleRow(listItem: 科目別計画ListItem): boolean {
      return this.getConfig(listItem) !== undefined && this.get補助科目(listItem).itemCount > 3;
    },
    addVisibleRow(勘定科目Pk: number): void {
      const invisible = this.科目別計画.list.find(
        (listItem) => listItem.勘定科目Pk === 勘定科目Pk && !this.isVisible科目別計画ListItem(listItem)
      );
      if (invisible === undefined) throw new Error();
      this.$set(this.getConfig(invisible), 'isVisible', true);
    },
    deleteVisibleRow(勘定科目Pk: number): void {
      if (this.itemFocus?.id !== 勘定科目Pk) return;
      const visibleItems = this.科目別計画.list.filter(
        (listItem) => listItem.勘定科目Pk === 勘定科目Pk && this.isVisible科目別計画ListItem(listItem)
      );
      if (visibleItems === undefined) throw new Error();
      if (visibleItems.length <= 3) return;
      const lastVisibleItem = visibleItems[visibleItems.length - 2];
      const configLastItem = this.getConfig(lastVisibleItem);
      const deleteItem = visibleItems[this.itemFocus.index - 1];
      const configDeleteItem = this.getConfig(deleteItem);

      const removeKey = this.isYearConfig ? 'yValue' : 'value';
      visibleItems.forEach((value, idx) => {
        // set config of idx = config of (idx + 1)
        if (idx < visibleItems.length - 2 && idx >= this.itemFocus!.index - 1) {
          const preConfigItem = this.getConfig(visibleItems[idx]);
          const configItem = this.getConfig(visibleItems[idx + 1]);
          this.$set(preConfigItem, 'label', configItem.label);
          this.$set(preConfigItem, 'type', configItem.type);
          if (configItem.isVisible) this.$set(preConfigItem, 'isVisible', configItem.isVisible);
          if (configItem.fixedValue) this.$set(preConfigItem, 'fixedValue', configItem.fixedValue);
          if (configItem.formula) this.$set(preConfigItem, 'formula', configItem.formula);
        }

        if (value.yearItems && Array.isArray(value.yearItems)) {
          if (idx == visibleItems.length - 2) {
            // remove value of last item
            value.yearItems.forEach((item) => {
              if (item && typeof item === 'object') this.$set(item.item, removeKey, 0);
              if (!this.isYearConfig) item.item.monthlyValues = [...Array(12)].fill(0);
            });
          } else if (idx < visibleItems.length - 2 && idx >= this.itemFocus!.index - 1) {
            // set value of idx = value of (idx + 1)
            value.yearItems.forEach((item, idxYear) => {
              const nextItem = visibleItems[idx + 1].yearItems[idxYear].item;
              if (item && typeof item === 'object') this.$set(item.item, removeKey, nextItem[removeKey]);
              if (!this.isYearConfig) item.item.monthlyValues = nextItem.monthlyValues;
            });
          }
        }
      });

      if (configLastItem && configDeleteItem) {
        // remove last config item
        this.$set(configLastItem, 'isVisible', false);
        this.$set(configLastItem, 'label', '');
        this.$set(configLastItem, 'type', E科目別計画明細ConfigType.直接入力);

        const configTotal = this.getConfig(visibleItems[visibleItems.length - 1]);
        if (configTotal && configTotal.type === E科目別計画明細ConfigType.計算式) {
          const indexs: number[] = [];
          const deleteNo = configDeleteItem.科目別計画明細No;
          configTotal.formula!.nos.map((value, idx) => {
            if (value == deleteNo) {
              indexs.push(idx);
            } else if (value > deleteNo) {
              configTotal.formula!.nos[idx] = value - 1;
            }
          });

          if (indexs.length > 0) {
            const nos = configTotal.formula!.nos.filter((_, idx) => !indexs.includes(idx));
            const operators = configTotal.formula!.operators.filter(
              (_, idx) => !indexs.includes(idx + 1) && !(indexs.includes(0) && idx == 0)
            );
            if (nos.length === 0) {
              delete configTotal.formula;
              this.$set(configTotal, 'type', 0);
            } else {
              this.$set(configTotal, 'formula', { nos: nos, operators: operators });
            }
          }
        }

        delete configLastItem.formula;
        delete configLastItem.fixedValue;
      }
      this.itemFocus = null;
    },
    openConfigDialog(listItem: 科目別計画ListItem): void {
      const listItemConfig = this.getConfig(listItem);

      let formula: 科目別計画明細InputFormula;
      if (listItemConfig.formula === undefined) {
        formula = {
          nos: new Array(FORMULA_NO_LENGTH).fill(undefined),
          operators: new Array(FORMULA_NO_LENGTH - 1).fill(undefined),
        };
      } else {
        formula = _.cloneDeep(listItemConfig.formula);
        while (formula.nos.length < FORMULA_NO_LENGTH) {
          formula.nos.push(undefined);
          formula.operators.push(undefined);
        }
      }

      const input: 科目別計画明細DialogConfig = {
        type: listItemConfig.type,
        fixedValue: listItemConfig.fixedValue ?? 0,
        formula,
      };
      this.configDialog = {
        visible: true,
        input,
        listItem,
        同科目ListItems: this.科目別計画!.list.filter(
          (li) => li.勘定科目Pk === listItem.勘定科目Pk && this.getConfig(li) !== undefined
        ),
      };
    },
    dialogFormulaNos(me: number): {
      label: number;
      value: number;
    }[] {
      return this.configDialog.同科目ListItems
        .filter((li) => {
          if (
            this.getConfig(li)?.科目別計画明細No === me ||
            !計画PlbscfService.isVisible科目別計画ListItem(li, this.isYearConfig)
          )
            return false;
          return !this.getConfig(li)?.formula?.nos.includes(me);
        })
        .map((li) => ({
          label: this.get計算項目No(li),
          value: this.getConfig(li).科目別計画明細No,
        }));
    },
    saveConfig(): void {
      const input = this.configDialog.input;
      const listItem = this.configDialog.listItem;
      if (input === null || listItem === null) return;

      const orgConfig = this.getConfig(listItem)!;
      if (![E科目別計画明細ConfigType.固定値, E科目別計画明細ConfigType.計算式].includes(input.type)) {
        orgConfig.fixedValue = undefined;
        orgConfig.formula = undefined;
      }
      switch (input.type) {
        case E科目別計画明細ConfigType.直接入力:
          if (!this.isYearConfig) {
            this.$_filterEditableYearPlbscfYearItems(listItem).forEach((yi) => {
              const monthlyValues = yi.item.monthlyValues;
              if (monthlyValues === undefined) return;
              yi.item.value = _.sum(monthlyValues);
            });
          }
          break;

        case E科目別計画明細ConfigType.固定値:
          // fixedValue を月分の固定値として直接入力可能データのみ適用
          const fixedValue = input.fixedValue ?? 0;
          orgConfig.fixedValue = fixedValue;
          orgConfig.formula = undefined;

          if (this.isYearConfig) {
            this.$_filterEditableYearPlbscfYearItems(listItem).forEach((yi) => {
              yi.item.yValue = fixedValue;
            });
            break;
          }

          this.$_filterEditableYearPlbscfYearItems(listItem).forEach((yi) => {
            if (yi.type === undefined) yi.item.value = fixedValue;
            const monthlyValues = yi.item.monthlyValues;
            if (monthlyValues === undefined) return;

            monthlyValues.forEach((_, i) => {
              if (this.$_isEditableMonth(i) && !this.disableMonths[i]) monthlyValues[i] = fixedValue;
            });
          });
          break;

        case E科目別計画明細ConfigType.計算式:
          const nos = input.formula.nos.filter((n): n is number => n !== undefined);
          const operators = input.formula.operators.filter((o): o is EOperator => o !== undefined);
          let checkEvaled = NaN;
          try {
            const checkFormula = 計画PlbscfService.createFormula(nos, operators, true);
            checkEvaled = eval(checkFormula);
          } catch (e) {
            // なにもしない
            console.warn(e);
          }
          if (isNaN(checkEvaled)) {
            alert('計算式として成立していません');
            return;
          }

          orgConfig.fixedValue = undefined;
          orgConfig.formula = { nos, operators };

          if (!this.isYearConfig) {
            const disMonths: number[] = [];
            this.$_filterEditableYearPlbscfYearItems(listItem).forEach((yi) => {
              const monthlyValues = yi.item.monthlyValues;
              if (monthlyValues === undefined) return;
              this.disableMonths.forEach((dis, i) => {
                if (dis) disMonths.push(i);
              });
            });
            orgConfig.disMonths = disMonths;
          }
          break;

        case E科目別計画明細ConfigType.前年値月平均:
          const copyFunction = (pyvFrom: PlbscfYearValue, yearItemTo: PlbscfYearItem): void => {
            const from月割りValues = 計画PlbscfService.calculate月割りValues(pyvFrom.item.value ?? 0);
            this.$_copyMvsToMvs(from月割りValues, yearItemTo);
          };
          this.$_copyMonthlyValues(listItem.通常科目Pk, listItem, true, copyFunction);
          break;

        case E科目別計画明細ConfigType.前年同月値:
          this.$_copyMonthlyValuesFromMonthlyValues(listItem.通常科目Pk, listItem, true);
          break;

        case E科目別計画明細ConfigType.前年値:
          this.$_copyYearValues(listItem.通常科目Pk, listItem, true);
          break;

        case E科目別計画明細ConfigType.科目選択:
          if (!this.is業績予測 && this.計画書.c計画表示モード === E計画表示モード.年次計画)
            this.$_copyYearValues(input.copy勘定科目Pk!, listItem, false);
          else this.$_copyMonthlyValuesFromMonthlyValues(input.copy勘定科目Pk!, listItem, false);
          break;
      }
      orgConfig.type = input.type;
      this.configDialog.visible = false;
      input.copy勘定科目Pk === undefined;
    },
    $_findPlbscfYearValuesBy勘定科目Pk(勘定科目Pk: number): PlbscfYearValue[] {
      return this.pl.list.find((l) => l.勘定科目?.pk === 勘定科目Pk)!.yearValues;
    },
    $_findPlbscfYearValueBy勘定科目PkAndYear(勘定科目Pk: number, year: number): PlbscfYearValue {
      return this.$_findPlbscfYearValuesBy勘定科目Pk(勘定科目Pk).find((yv) => yv.year === year)!;
    },
    /**
     * 月次値コピー
     *
     * @param 勘定科目PkFrom
     * @param listItemTo
     * @param isLastYearFrom コピー元が前年値か否か
     * @param copyFunction コピー関数
     */
    $_copyMonthlyValues(
      勘定科目PkFrom: number,
      listItemTo: 科目別計画ListItem,
      isLastYearFrom: boolean,
      copyFunction: (pyvFrom: PlbscfYearValue, yearItemTo: PlbscfYearItem) => void
    ): void {
      let yearTo = 0;
      if (this.is業績予測) {
        // モニタリング
        yearTo = this.モニタリング会計期間Year;
      } else {
        // 計画
        yearTo = 決算期Utility.get計画0期の決算年();
        if (this.計画書.c計画表示モード === E計画表示モード.月次計画第1期) yearTo++;
      }
      const yearFrom = isLastYearFrom ? yearTo - 1 : yearTo;

      const pyvFrom = this.$_findPlbscfYearValueBy勘定科目PkAndYear(勘定科目PkFrom, yearFrom);
      const yearItemTo = listItemTo.yearItems.find((yi) => yi.year === yearTo)!;
      copyFunction(pyvFrom, yearItemTo);
    },
    /**
     * 月次値から月次値にコピー
     *
     * @param 勘定科目PkFrom
     * @param listItemTo
     * @param isLastYearFrom コピー元が前年値か否か
     */
    $_copyMonthlyValuesFromMonthlyValues(
      勘定科目PkFrom: number,
      listItemTo: 科目別計画ListItem,
      isLastYearFrom: boolean
    ): void {
      const copyFunction = (pyvFrom: PlbscfYearValue, yearItemTo: PlbscfYearItem): void => {
        const monthlyValues = pyvFrom.item.monthlyValues ?? Array(MONTHS_OF_YEAR).fill(0);
        this.$_copyMvsToMvs(monthlyValues, yearItemTo);
      };
      this.$_copyMonthlyValues(勘定科目PkFrom, listItemTo, isLastYearFrom, copyFunction);
    },
    /**
     * 年次値コピー
     * ※モニタリングでは使用しない想定
     *
     * @param 勘定科目PkFrom
     * @param listItemTo
     * @param isLastYearFrom コピー元が前年値か否か
     */
    $_copyYearValues(勘定科目PkFrom: number, listItemTo: 科目別計画ListItem, isLastYearFrom: boolean): void {
      const yearValuesFrom = this.$_findPlbscfYearValuesBy勘定科目Pk(勘定科目PkFrom);
      this.$_filterEditableYearPlbscfYearItems(listItemTo).forEach((yi) => {
        const fromY = isLastYearFrom ? yi.year - 1 : yi.year;
        yi.item.yValue = yearValuesFrom.find((yv) => yv.year === fromY)?.item.yValue ?? 0;
        // 第1期は月次分割が必要
        // if (yi.year === 決算期Utility.get計画0期の決算年() + 1) 計画PlbscfService.divideYearlyValue(yi.item, yearValue);
        // else yi.item.value = yearValue;
      });
    },
    $_filterEditableYearPlbscfYearItems(listItem: 科目別計画ListItem): PlbscfYearItem[] {
      let firstY = 0;
      let lastY = 0;
      if (this.is業績予測) {
        // モニタリング
        if (this.readonlyMonthIndex === MONTHS_OF_YEAR - 1) return [];
        firstY = this.モニタリング会計期間Year;
        lastY = firstY;
      } else {
        // 計画
        firstY = 決算期Utility.get計画0期の決算年();
        lastY = firstY;
        if (this.計画書.c計画表示モード !== E計画表示モード.月次計画第0期) {
          firstY++;
          if (this.計画書.c計画表示モード === E計画表示モード.年次計画) {
            firstY--;
            if (this.計画書.c第1期月次作成フラグ === E有無フラグ.あり) firstY++;
            lastY += this.事業所.c計画作成期間;
          } else lastY++;
        }
      }
      return listItem.yearItems.filter((yi) => yi.year >= firstY && yi.year <= lastY);
    },
    $_isEditableMonth(monthIndex: number): boolean {
      return this.readonlyMonthIndex === null || monthIndex > this.readonlyMonthIndex;
    },
    $_copyMvsToMvs(mvsFrom: number[], yearItemTo: PlbscfYearItem): void {
      const mvs = yearItemTo.item.monthlyValues;
      if (mvs === undefined) return;
      mvs.forEach((_, i) => {
        if (this.$_isEditableMonth(i) && !this.disableMonths[i]) mvs[i] = mvsFrom[i];
      });
      yearItemTo.item.value = _.sum(mvs);
    },
    onFocusLine(id: number, index: number): void {
      if (index <= 2 || index == 10) return;
      this.itemFocus = {
        id: id,
        index: index,
      };
      this.isFocused = true;
    },
    onBlurLine(): void {
      this.isFocused = false;
    },
    onOutsideButton(): void {
      if (!this.isFocused) this.itemFocus = null;
    },
  },
});
